<template>
	<v-card class="ma-5" flat :loading="loading">
		<v-card-text class="pa-0">
			<div class="panel-content px-4 py-4">
				<!-- Current Organization Integration Section -->
				<div class="mb-4">
					<div class="d-flex align-center mb-2">
						<span class="font-weight-bold">{{ $t("current-organization") }}</span>
					</div>

					<v-alert
						v-if="!currentOrgIntegrations.length && !loading"
						type="info"
						outlined
						dense
					>
						{{ $t("no-current-erp-integration") }}
					</v-alert>

					<v-list v-else-if="currentOrgIntegrations.length" class="pa-0">
						<v-list-item
							v-for="currentOrgIntegration in currentOrgIntegrations"
							:key="currentOrgIntegration.id"
							class="integration-item current-org-integration mb-2"
						>
							<v-list-item-content>
								<v-list-item-title class="d-flex align-center">
									<span class="font-weight-medium mr-2">
										{{ currentOrgIntegration?.organizationName["fr-FR"] }}
									</span>
									<v-chip x-small color="info" class="mr-2">
										{{ $t("current") }}
									</v-chip>
									<v-spacer />
									<!-- Action menu -->
									<v-menu offset-y left>
										<template #activator="{ on, attrs }">
											<v-btn icon x-small v-bind="attrs" v-on="on">
												<v-icon small>mdi-dots-vertical</v-icon>
											</v-btn>
										</template>
										<v-list dense>
											<v-list-item
												:disabled="loading"
												@click="openErpOrgIdDialog(currentOrgIntegration)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="primary">mdi-identifier</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("edit-erp-organization-id") }}</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-if="isAvancieType(currentOrgIntegration)"
												:disabled="loading || syncingId !== null"
												@click="confirmSynchronize(currentOrgIntegration)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="info">mdi-sync</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("synchronize") }}</v-list-item-title>
											</v-list-item>
											<v-list-item
												:disabled="loading || resettingId !== null || disablingId !== null"
												@click="confirmResetLastTransfer(currentOrgIntegration)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="warning">mdi-history</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("reset") }} lastTransfer</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-if="currentOrgIntegration.enabled"
												:disabled="loading || resettingId !== null || disablingId !== null"
												@click="confirmDisableIntegration(currentOrgIntegration)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="error">mdi-power</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("disable-integration") }}</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-else
												:disabled="loading || resettingId !== null || disablingId !== null"
												@click="confirmEnableIntegration(currentOrgIntegration)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="success">mdi-power</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("enable-integration") }}</v-list-item-title>
											</v-list-item>
											<v-divider />
											<v-list-item
												:disabled="loading || resettingId !== null || disablingId !== null || syncingId !== null || savingErpOrgId"
												@click="confirmRemoveErpIntegration(currentOrgIntegration)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="error">mdi-delete</v-icon>
												</v-list-item-icon>
												<v-list-item-title class="error--text">
													{{ $t("remove-erp-integration") }}
												</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-menu>
								</v-list-item-title>
								<v-list-item-title class="d-flex align-center">
									<v-chip
										:color="currentOrgIntegration.enabled ? 'primary' : 'error'"
										x-small
										class="mr-2"
									>
										{{ currentOrgIntegration.enabled ? $t("enabled") : $t("disabled") }}
									</v-chip>
									<v-chip color="secondary" x-small class="mr-2">
										{{ currentOrgIntegration.erpLabel || currentOrgIntegration.erpType }}
									</v-chip>
								</v-list-item-title>
								<v-list-item-subtitle class="mt-1">
									<div class="d-flex flex-column">
										<span class="text-caption grey--text">
											<strong>{{ $t("organization-id") }}:</strong> {{ currentOrgIntegration.organizationId }}
										</span>
										<span class="text-caption grey--text">
											<strong>{{ $t("erp-integration-id") }}:</strong> {{ currentOrgIntegration.id }}
											<v-icon
												small
												class="ml-1 copy-icon"
												@click.stop="copyToClipboard(currentOrgIntegration.id)"
											>mdi-content-copy</v-icon>
										</span>
										<span v-if="currentOrgIntegration.erpOrganizationId" class="text-caption grey--text">
											<strong>{{ $t("erp-organization-id") }}:</strong> {{ currentOrgIntegration.erpOrganizationId }}
											<v-icon
												small
												class="ml-1 copy-icon"
												@click.stop="copyToClipboard(currentOrgIntegration.erpOrganizationId)"
											>mdi-content-copy</v-icon>
										</span>
										<span class="text-caption grey--text">
											<strong>{{ $t("last-transfer") }}:</strong>
											{{ currentOrgIntegration.lastTransfer ? formatDate(currentOrgIntegration.lastTransfer) : $t("never") }}
											<v-icon
												v-if="isTransferStale(currentOrgIntegration.lastTransfer)"
												small
												color="warning"
												class="ml-1"
											>mdi-alert</v-icon>
										</span>
										<!-- Query field with eye icon -->
										<span class="text-caption grey--text d-flex align-center">
											<strong>{{ $t("query") }}:</strong>
											<v-btn
												icon
												x-small
												class="ml-1"
												:loading="queryLoadingId === currentOrgIntegration.id"
												@click.stop="toggleQuery(currentOrgIntegration)"
											>
												<v-icon small color="primary">
													{{ expandedQueryId === currentOrgIntegration.id ? 'mdi-eye-off' : 'mdi-eye' }}
												</v-icon>
											</v-btn>
											<v-icon
												v-if="expandedQueryId === currentOrgIntegration.id"
												small
												class="ml-1 copy-icon"
												@click.stop="copyToClipboard(integrationQueries[currentOrgIntegration.id]?.query || '')"
											>mdi-content-copy</v-icon>
										</span>
									</div>
								</v-list-item-subtitle>

								<!-- Query display section -->
								<div v-if="expandedQueryId === currentOrgIntegration.id" class="query-section mt-2">
									<pre class="query-content text-caption">{{ formatQuery(integrationQueries[currentOrgIntegration.id]?.query) }}</pre>
								</div>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</div>

				<v-divider class="mb-4" />

				<!-- Other Integrations Section -->
				<div>
					<div
						class="d-flex align-center mb-2 other-integrations-header"
						@click="toggleOtherIntegrations"
					>
						<v-icon small class="mr-2">
							{{ showOtherIntegrations ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
						</v-icon>
						<span class="font-weight-bold">{{ $t("other-erp-integrations") }}</span>
						<v-chip x-small class="ml-2" color="grey lighten-2">
							{{ otherIntegrations.length }}
						</v-chip>
						<v-progress-circular
							v-if="loadingOtherIntegrations"
							indeterminate
							size="16"
							width="2"
							class="ml-2"
						/>
						<v-spacer />

						<!-- Filter controls (only show when expanded) -->
						<template v-if="showOtherIntegrations && otherIntegrations.length > 0">
							<!-- Results count -->
							<span class="text-caption grey--text mr-2">
								{{ filteredOtherIntegrations.length }} / {{ otherIntegrations.length }}
							</span>

							<!-- Active filter chips -->
							<v-chip
								v-if="showOnlyStale"
								small
								close
								color="primary"
								class="mr-2"
								@click.stop
								@click:close="showOnlyStale = false"
							>
								7+ {{ $t("days") }}
							</v-chip>
							<v-chip
								v-if="showOnlyDisabled"
								small
								close
								color="primary"
								class="mr-2"
								@click.stop
								@click:close="showOnlyDisabled = false"
							>
								{{ $t("disabled") }}
							</v-chip>
							<v-chip
								v-for="erpType in selectedErpTypes"
								:key="erpType"
								small
								close
								color="primary"
								class="mr-2 text-capitalize"
								@click.stop
								@click:close="toggleErpType(erpType)"
							>
								{{ erpType }}
							</v-chip>

							<v-menu offset-y :close-on-content-click="false">
								<template #activator="{ on, attrs }">
									<v-badge
										:value="activeFilterCount > 0"
										:content="activeFilterCount"
										color="primary"
										overlap
									>
										<v-btn
											icon
											small
											v-bind="attrs"
											v-on="on"
											@click.stop
										>
											<v-icon>mdi-filter</v-icon>
										</v-btn>
									</v-badge>
								</template>
								<v-list dense>
									<v-list-item @click="showOnlyStale = !showOnlyStale">
										<v-list-item-action class="mr-2">
											<v-checkbox
												v-model="showOnlyStale"
												hide-details
												dense
											/>
										</v-list-item-action>
										<v-list-item-title>{{ $t("filter-stale-transfers") }}</v-list-item-title>
									</v-list-item>
									<v-list-item @click="showOnlyDisabled = !showOnlyDisabled">
										<v-list-item-action class="mr-2">
											<v-checkbox
												v-model="showOnlyDisabled"
												hide-details
												dense
											/>
										</v-list-item-action>
										<v-list-item-title>{{ $t("filter-disabled") }}</v-list-item-title>
									</v-list-item>
									<v-divider class="my-1" />
									<v-subheader>{{ $t("erp-type") }}</v-subheader>
									<v-list-item
										v-for="erpType in erpTypes"
										:key="erpType"
										@click="toggleErpType(erpType)"
									>
										<v-list-item-action class="mr-2">
											<v-checkbox
												:input-value="selectedErpTypes.includes(erpType)"
												hide-details
												dense
											/>
										</v-list-item-action>
										<v-list-item-title class="text-capitalize">
											{{ erpType }}
										</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</div>

					<template v-if="showOtherIntegrations">
						<v-alert
							v-if="!filteredOtherIntegrations.length && !loadingOtherIntegrations"
							type="info"
							outlined
							dense
							class="mb-4"
						>
							{{ $t("no-erp-integrations-found") }}
						</v-alert>

						<v-list v-else class="pa-0">
							<div v-for="(integration, index) in filteredOtherIntegrations" :key="`${integration.id}-${index}`">
								<v-list-item class="integration-item mb-2">
									<v-list-item-content>
										<v-list-item-title class="d-flex align-center">
											<span class="font-weight-medium mr-2">
												{{ integration?.organizationName["fr-FR"] }}
											</span>
											<v-spacer />
											<v-menu offset-y left>
												<template #activator="{ on, attrs }">
													<v-btn icon x-small v-bind="attrs" v-on="on">
														<v-icon small>mdi-dots-vertical</v-icon>
													</v-btn>
												</template>
												<v-list dense>
													<v-list-item
														:disabled="loading || resettingId !== null || disablingId !== null"
														@click="openErpOrgIdDialog(integration)"
													>
														<v-list-item-icon class="mr-2">
															<v-icon small color="secondary">mdi-identifier</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ $t("edit-erp-organization-id") }}</v-list-item-title>
													</v-list-item>
													<v-list-item
														:disabled="loading || resettingId !== null || disablingId !== null"
														@click="confirmResetLastTransfer(integration)"
													>
														<v-list-item-icon class="mr-2">
															<v-icon small color="warning">mdi-history</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ $t("reset") }} lastTransfer</v-list-item-title>
													</v-list-item>
													<v-list-item
														v-if="integration.enabled"
														:disabled="loading || resettingId !== null || disablingId !== null"
														@click="confirmDisableIntegration(integration)"
													>
														<v-list-item-icon class="mr-2">
															<v-icon small color="error">mdi-power</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ $t("disable-integration") }}</v-list-item-title>
													</v-list-item>
													<v-list-item
														v-else
														:disabled="loading || resettingId !== null || disablingId !== null"
														@click="confirmEnableIntegration(integration)"
													>
														<v-list-item-icon class="mr-2">
															<v-icon small color="success">mdi-power</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ $t("enable-integration") }}</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</v-list-item-title>
										<v-list-item-title class="d-flex align-center">
											<v-chip
												:color="integration.enabled ? 'primary' : 'error'"
												x-small
												class="mr-2"
											>
												{{ integration.enabled ? $t("enabled") : $t("disabled") }}
											</v-chip>
											<v-chip
												color="secondary"
												x-small
												class="mr-2"
											>
												{{ integration.erpLabel || integration.erpType }}
											</v-chip>
										</v-list-item-title>
										<v-list-item-subtitle class="mt-1">
											<div class="d-flex flex-column">
												<span class="text-caption grey--text">
													<strong>{{ $t("organization-id") }}:</strong> {{ integration.organizationId }}
												</span>
												<span class="text-caption grey--text">
													<strong>{{ $t("erp-integration-id") }}:</strong> {{ integration.id }}
													<v-icon
														small
														class="ml-1 copy-icon"
														@click.stop="copyToClipboard(integration.id)"
													>mdi-content-copy</v-icon>
												</span>
												<span v-if="integration.erpOrganizationId" class="text-caption grey--text">
													<strong>{{ $t("erp-organization-id") }}:</strong> {{ integration.erpOrganizationId }}
													<v-icon
														small
														class="ml-1 copy-icon"
														@click.stop="copyToClipboard(integration.erpOrganizationId)"
													>mdi-content-copy</v-icon>
												</span>
												<span class="text-caption grey--text">
													<strong>{{ $t("last-transfer") }}:</strong>
													{{ integration.lastTransfer ? formatDate(integration.lastTransfer) : $t("never") }}
													<v-icon
														v-if="isTransferStale(integration.lastTransfer)"
														small
														color="warning"
														class="ml-1"
													>mdi-alert</v-icon>
												</span>
												<!-- Query field with eye icon -->
												<span class="text-caption grey--text d-flex align-center">
													<strong>{{ $t("query") }}:</strong>
													<v-btn
														icon
														x-small
														class="ml-1"
														:loading="queryLoadingId === integration.id"
														@click.stop="toggleQuery(integration)"
													>
														<v-icon small color="primary">
															{{ expandedQueryId === integration.id ? 'mdi-eye-off' : 'mdi-eye' }}
														</v-icon>
													</v-btn>
													<v-icon
														v-if="expandedQueryId === integration.id"
														small
														class="ml-1 copy-icon"
														@click.stop="copyToClipboard(integrationQueries[integration.id]?.query || '')"
													>mdi-content-copy</v-icon>
												</span>
											</div>
										</v-list-item-subtitle>

										<!-- Query display section -->
										<div v-if="expandedQueryId === integration.id" class="query-section mt-2">
											<pre class="query-content text-caption">{{ formatQuery(integrationQueries[integration.id]?.query) }}</pre>
										</div>
									</v-list-item-content>
								</v-list-item>
								<v-divider class="my-1" />
							</div>
						</v-list>
					</template>
				</div>
			</div>
		</v-card-text>

		<confirmation-dialog ref="dialogRef" content-class="devops-subdialog" />

		<!-- Edit ERP Organization ID Dialog -->
		<v-dialog v-model="erpOrgIdDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('edit-erp-organization-id') }}
					<v-spacer />
					<v-btn icon small @click="closeErpOrgIdDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-text-field
						v-model="erpOrgIdValue"
						:label="$t('erp-organization-id')"
						outlined
						dense
						hide-details
					/>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						:disabled="savingErpOrgId"
						@click="closeErpOrgIdDialog"
					>
						{{ $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:loading="savingErpOrgId"
						:disabled="savingErpOrgId"
						class="px-4"
						@click="saveErpOrgId"
					>
						{{ $t('save') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import ConfirmationDialog from "@/components/configuration/ConfirmationDialog.vue";

export default {
	name: "ErpIntegrationPanel",

	components: {
		ConfirmationDialog
	},

	data() {
		return {
			currentOrgIntegrations: [],
			otherIntegrations: [],
			loading: false,
			loadingOtherIntegrations: false,
			showOtherIntegrations: false,
			otherIntegrationsFetched: false,
			queryLoadingId: null,
			expandedQueryId: null,
			integrationQueries: {},
			disablingId: null,
			resettingId: null,
			syncingId: null,
			savingErpOrgId: false,
			showOnlyStale: false,
			showOnlyDisabled: false,
			selectedErpTypes: [],
			erpTypes: [],
			erpOrgIdDialog: false,
			erpOrgIdValue: "",
			erpOrgIdIntegration: null
		};
	},

	computed: {
		filteredOtherIntegrations() {
			let result = this.otherIntegrations;

			if (this.showOnlyStale) {
				result = result.filter(integration => this.isTransferStale(integration.lastTransfer));
			}
			if (this.showOnlyDisabled) {
				result = result.filter(integration => !integration.enabled);
			}
			if (this.selectedErpTypes.length > 0) {
				result = result.filter(integration =>
					this.selectedErpTypes.includes(integration.erpType?.toLowerCase())
				);
			}

			return result;
		},

		activeFilterCount() {
			let count = 0;

			if (this.showOnlyStale) count++;
			if (this.showOnlyDisabled) count++;
			if (this.selectedErpTypes.length > 0) count++;
			return count;
		}
	},

	async created() {
		await Promise.all([
			this.fetchCurrentOrgIntegration(),
			this.fetchErpTypes(),
			this.fetchOtherIntegrations()
		]);
	},

	methods: {
		async fetchIntegrations(options) {
			const silent = options?.silent || false;

			if (!silent) this.otherIntegrationsFetched = false;
			const results = await Promise.allSettled([
				this.fetchCurrentOrgIntegration({ silent }),
				this.fetchOtherIntegrations({ silent })
			]);

			const hasError = results.some(r =>
				r.status === "rejected" || (r.status === "fulfilled" && r.value?.success === false)
			);

			return { success: !hasError };
		},

		async fetchErpTypes() {
			try {
				const { data } = await this.axios.get("/devops/erp-types");

				this.erpTypes = data;
			} catch (err) {
				console.error("Error fetching ERP types:", err);
			}
		},

		toggleErpType(erpType) {
			const index = this.selectedErpTypes.indexOf(erpType);

			if (index === -1) {
				this.selectedErpTypes.push(erpType);
			} else {
				this.selectedErpTypes.splice(index, 1);
			}
		},

		async fetchCurrentOrgIntegration(options) {
			const silent = options?.silent || false;

			if (!silent) this.loading = true;
			try {
				const { data } = await this.axios.get("/devops/erp-integrations/current");

				this.currentOrgIntegrations = data;
				return { success: true };
			} catch (err) {
				console.error("Error fetching current org integration:", err);
				if (!silent) this.$error(this.$t("error.fetch-erp-integrations"));
				return { success: false };
			} finally {
				if (!silent) this.loading = false;
			}
		},

		async fetchOtherIntegrations(options) {
			const silent = options?.silent || false;

			if (!silent && this.otherIntegrationsFetched) return { success: true };

			if (!silent) this.loadingOtherIntegrations = true;
			try {
				const { data } = await this.axios.get("/devops/erp-integrations/others");

				this.otherIntegrations = data;
				this.otherIntegrationsFetched = true;
				return { success: true };
			} catch (err) {
				console.error("Error fetching other integrations:", err);
				if (!silent) this.$error(this.$t("error.fetch-erp-integrations"));
				return { success: false };
			} finally {
				if (!silent) this.loadingOtherIntegrations = false;
			}
		},

		async toggleOtherIntegrations() {
			this.showOtherIntegrations = !this.showOtherIntegrations;

			if (this.showOtherIntegrations && !this.otherIntegrationsFetched) {
				await this.fetchOtherIntegrations();
			}
		},

		isAvancieType(integration) {
			return integration?.erpType?.toLowerCase() === "avancie";
		},

		async copyToClipboard(text) {
			try {
				const textToCopy = typeof text === "object" ? JSON.stringify(text, null, 2) : text;

				await navigator.clipboard.writeText(textToCopy);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (error) {
				this.$error(this.$t("error.copy-failed"));
			}
		},

		formatQuery(query) {
			if (!query) return this.$t("no-query-available");
			if (typeof query === "object") return JSON.stringify(query, null, 2);
			return query;
		},

		async toggleQuery(integration) {
			if (this.expandedQueryId === integration.id) {
				this.expandedQueryId = null;
				return;
			}

			// If we haven't fetched this integration's query yet, fetch it
			if (!this.integrationQueries[integration.id]) {
				this.queryLoadingId = integration.id;
				try {
					const { data } = await this.axios.get(`/patients/integrations/devops/erp/${integration.erpId}`);

					this.$set(this.integrationQueries, integration.id, {
						query: data.query || null
					});
				} catch (err) {
					console.error("Error fetching integration query:", err);
					this.$error(this.$t("error.fetch-erp-query"));
					this.$set(this.integrationQueries, integration.id, { query: null });
				} finally {
					this.queryLoadingId = null;
				}
			}

			this.expandedQueryId = integration.id;
		},

		formatDate(dateString) {
			if (!dateString) return this.$t("never");

			const date = new Date(dateString);

			return date.toLocaleString(this.$i18n.locale, {
				year: "numeric",
				month: "short",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit"
			});
		},

		isTransferStale(dateString) {
			if (!dateString) return true;

			const date = new Date(dateString);
			const now = new Date();
			const diffMs = now - date;
			const diffDays = diffMs / (1000 * 60 * 60 * 24);

			return diffDays >= 7;
		},

		async confirmResetLastTransfer(integration) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("reset-last-transfer-title"),
					body: this.$t("reset-last-transfer-confirm", { organization: integration.organizationName["fr-FR"] }),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			await this.resetLastTransfer(integration);
		},

		async resetLastTransfer(integration) {
			this.resettingId = integration.id;
			try {
				const { data } = await this.axios.post(`/devops/erp-integrations/${integration.id}/reset-transfer`);

				this.updateIntegrationData(integration.id, data);

				this.$success(this.$t("reset-last-transfer-success"));
			} catch (err) {
				console.error("Error resetting last transfer:", err);
				this.$error(this.$t("error.reset-last-transfer"));
			} finally {
				this.resettingId = null;
			}
		},

		async confirmRemoveErpIntegration(integration) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("remove-erp-integration"),
					body: this.$t("remove-erp-integration-confirm", { organization: integration.organizationName["fr-FR"] }),
					confirm: this.$t("remove"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			this.loading = true;
			try {
				const { erpType, organizationId } = integration;

				await this.axios.delete(`/patients/integrations/admin/rest/${encodeURIComponent(erpType)}/${encodeURIComponent(organizationId)}`, {
					params: { integrationId: integration.id }
				});

				this.currentOrgIntegrations = this.currentOrgIntegrations.filter(current => current.id !== integration.id);
				if (this.expandedQueryId === integration.id) this.expandedQueryId = null;
				this.$delete(this.integrationQueries, integration.id);
				this.$success(this.$t("remove-erp-integration-success"));
			} catch (err) {
				console.error("Error removing ERP integration:", err);
				this.$error(err.response?.data?.message || this.$t("error.remove-erp-integration"));
			} finally {
				this.loading = false;
			}
		},

		async confirmDisableIntegration(integration) {
			const confirmed = await this.$refs.dialogRef.open({
				title: this.$t("disable-integration"),
				message: this.$t("disable-integration-confirmation", { name: integration.organizationName["fr-FR"] }),
				confirmLabel: this.$t("disable"),
				confirmColor: "error",
				showCloseButton: true
			});

			if (confirmed) {
				await this.setIntegrationEnabled(integration, false);
			}
		},

		async confirmEnableIntegration(integration) {
			const confirmed = await this.$refs.dialogRef.open({
				title: this.$t("enable-integration"),
				message: this.$t("enable-integration-confirmation", { name: integration.organizationName["fr-FR"] }),
				confirmLabel: this.$t("enable"),
				confirmColor: "success",
				showCloseButton: true
			});

			if (confirmed) {
				await this.setIntegrationEnabled(integration, true);
			}
		},

		async setIntegrationEnabled(integration, enabled) {
			this.disablingId = integration.id;
			try {
				const { data } = await this.axios.patch(`/devops/erp-integrations/${integration.id}`, { enabled });

				this.updateIntegrationData(integration.id, data);

				this.$success(this.$t(enabled ? "integration-enabled" : "integration-disabled"));
			} catch (err) {
				console.error("Error updating integration:", err);
				this.$error(this.$t("error.update-integration"));
			} finally {
				this.disablingId = null;
			}
		},

		async confirmSynchronize(integration) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("synchronize-avancie-title"),
					body: this.$t("synchronize-avancie-confirm", { organization: integration.organizationName["fr-FR"] }),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			await this.synchronizeAvancie(integration);
		},

		async synchronizeAvancie(integration) {
			this.syncingId = integration.id;
			try {
				await this.axios.post("/patients/integrations/avancie/synchronize");

				this.$success(this.$t("synchronize-avancie-success"));
			} catch (err) {
				console.error("Error synchronizing Avancie:", err);
				this.$error(this.$t("error.synchronize-avancie"));
			} finally {
				this.syncingId = null;
			}
		},

		openErpOrgIdDialog(integration) {
			this.erpOrgIdIntegration = integration;
			this.erpOrgIdValue = integration.erpOrganizationId || "";
			this.erpOrgIdDialog = true;
		},

		closeErpOrgIdDialog() {
			this.erpOrgIdDialog = false;
			this.erpOrgIdValue = "";
			this.erpOrgIdIntegration = null;
		},

		async saveErpOrgId() {
			if (!this.erpOrgIdIntegration) return;

			this.savingErpOrgId = true;
			try {
				const { erpType, organizationId } = this.erpOrgIdIntegration;

				await this.axios.put(`/patients/integrations/admin/rest/${erpType}/${organizationId}`, {
					erpOrganizationId: this.erpOrgIdValue || null
				}, {
					params: { integrationId: this.erpOrgIdIntegration.id }
				});

				// Update the erpOrganizationId locally instead of relying on response data structure
				const integration = [...this.currentOrgIntegrations, ...this.otherIntegrations]
					.find(current => current.id === this.erpOrgIdIntegration.id);

				if (integration) this.$set(integration, "erpOrganizationId", this.erpOrgIdValue || null);

				this.$success(this.$t("erp-org-id-saved"));
				this.closeErpOrgIdDialog();
			} catch (err) {
				console.error("Error saving ERP Organization ID:", err);
				this.$error(this.$t("error.save-erp-org-id"));
			} finally {
				this.savingErpOrgId = false;
			}
		},

		updateIntegrationData(integrationId, data) {
			// Check if it's the current org integration
			const currentIndex = this.currentOrgIntegrations.findIndex(integration => integration.id === integrationId);

			if (currentIndex !== -1) {
				this.$set(this.currentOrgIntegrations, currentIndex, data);
				return;
			}

			// Otherwise update in other integrations list
			const index = this.otherIntegrations.findIndex(i => i.id === integrationId);

			if (index !== -1) {
				this.$set(this.otherIntegrations, index, data);
			}
		}
	}
};
</script>

<style scoped>
.panel-content {
	max-height: 750px;
	overflow-y: auto;
	overflow-x: hidden;
}

@media (max-width: 599px) {
	.panel-content {
		max-height: none;
	}
}

.integration-item {
	border-radius: 8px;
	background-color: #fafafa;
	height: auto !important;
	min-height: unset !important;
}

.integration-item.current-org-integration {
	background-color: #e3f2fd;
	border-left: 3px solid #1976d2;
}

.integration-item.current-org-integration:hover {
	background-color: #bbdefb;
}

.integration-item >>> .v-list-item__content {
	height: auto !important;
	min-height: unset !important;
	align-self: stretch !important;
}

.integration-item:not(.current-org-integration):hover {
	background-color: #f0f0f0;
}

.copy-icon {
	cursor: pointer;
	opacity: 0.7;
}

.copy-icon:hover {
	opacity: 1;
}

.query-section {
	background-color: #f5f5f5;
	border-radius: 4px;
	padding: 8px;
	width: 100%;
}

.query-content {
	background-color: #ffffff;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 8px;
	overflow-x: auto;
	white-space: pre-wrap;
	word-break: break-all;
	max-height: 200px;
	overflow-y: auto;
}

.other-integrations-header {
	cursor: pointer;
	padding: 8px;
	border-radius: 4px;
	transition: background-color 0.2s;
}

.other-integrations-header:hover {
	background-color: #f5f5f5;
}
</style>
